// src/components/resources/calculators/VideoDropoffCalculator.tsx

/**
 * @file src/components/resources/calculators/VideoDropoffCalculator.tsx
 * @description A client-side component for calculating and visualizing video retention and drop-off rates.
 *              Allows users to input video metrics from various platforms (Facebook, TikTok, YouTube, Vimeo) or custom data,
 *              and provides interactive charts, key performance indicators, and platform-specific insights.
 */
'use client'

import { clientLogger } from '~/lib/logger-client'
import { useState, useMemo, useCallback, useEffect } from 'react'
import { motion } from 'motion/react'
import Link from 'next/link'
import { cn } from '~/lib/utils'
// Direct icon imports to avoid loading all 200+ icons from the barrel export
// This reduces bundle size by only including the icons actually used
import RiAddLine from "~/icons/remix/generated/RiAddLine"
import RiBarChartLine from "~/icons/remix/generated/RiBarChartLine"
import { FAQ } from '~/components/shared/FAQ'
import { EmbeddedEmailCapture } from '~/components/forms/variants/embedded/EmbeddedEmailCapture'
import type { EmbeddedEmailCaptureProps } from '~/components/forms/variants/embedded/types'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'
import { debounce } from '~/lib/utils/events'
// Extracted components for better organization
import { PlatformSelector } from './video-dropoff/components/PlatformSelector'
import { BasicInputs } from './video-dropoff/components/BasicInputs'
import { BreakpointsTable } from './video-dropoff/components/BreakpointsTable'
import { ResultsSection } from './video-dropoff/components/ResultsSection'
import type { 
  Breakpoint, 
  CalculatorInputs, 
  CalculatorResults,
  ChartView,
  DropoffRateItem
} from './video-dropoff/types'
import { FAQ_ITEMS } from './video-dropoff/types/constants'
import { generatePlatformBreakpoints } from './video-dropoff/utils/platform-breakpoints'
import { calculateMetrics } from './video-dropoff/utils/calculations'
import { prepareChartData } from './video-dropoff/utils/chart-data'
// Related-link entries live in a sibling `.related.ts` so the slugs
// can be unit-tested against the live registries. See
// `src/lib/resources/__tests__/related-link-slugs.test.ts`.
import {
  VIDEO_DROPOFF_RELATED_GLOSSARY,
  VIDEO_DROPOFF_RELATED_TOOLS,
} from './VideoDropoffCalculator.related'


// Main Video Dropoff Calculator component
/**
 * Props for the video drop-off calculator. All optional so the umbrella
 * `video-dropoff-calculator` page renders config-free, while the
 * `video-completion-rate-calculator` SEO landing variant drives the
 * heading and FAQ from its own data record. The route passes `faqItems`
 * straight from the calculator's registry `faqs`, so the VISIBLE FAQ and
 * the `FAQPage` JSON-LD share a single source (the registry) and can't
 * drift apart.
 */
export interface VideoDropoffCalculatorProps {
  /** In-tool H2. Defaults to the umbrella's "Video Drop-off Calculator". */
  heading?: string
  /** Sub-heading under the H2. */
  subheading?: string
  /** FAQ entries to render. Passed from the registry record so visible
   *  copy matches the JSON-LD; when empty the FAQ block is omitted. */
  faqItems?: { question: string; answer: string }[]
  /** Slug used for analytics/CRM so the variant reports its own
   *  CTR/conversion signal instead of folding into the umbrella. */
  trackingSlug?: string
}

export function VideoDropoffCalculator({
  heading = 'Video Drop-off Calculator',
  subheading = 'Analyze where your audience drops off and stops watching — across YouTube, Vimeo, TikTok, and Facebook',
  faqItems = FAQ_ITEMS.map(item => ({ ...item })),
  trackingSlug = 'video-dropoff-calculator',
}: VideoDropoffCalculatorProps = {}): JSX.Element {
  // Component state
  const [inputs, setInputs] = useState<CalculatorInputs>({
    platform: 'facebook',
    videoLength: 60, // 60 seconds by default
    totalViews: 1000,
    breakpoints: generatePlatformBreakpoints('facebook', 60, 1000)
  })

  // Update breakpoints when platform, video length or total views change
  useEffect(() => {
    // Only update breakpoints if this isn't a custom platform where user manages breakpoints
    if (inputs.platform !== 'custom' || inputs.breakpoints.length <= 1) {
      const newBreakpoints = generatePlatformBreakpoints(
        inputs.platform, 
        inputs.videoLength, 
        inputs.totalViews
      )
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional client-side state update inside an effect (SSR-safe staging of derived UI state)
      setInputs(prev => ({ ...prev, breakpoints: newBreakpoints }))
    }
  }, [inputs.platform, inputs.videoLength, inputs.totalViews, inputs.breakpoints.length])

  const [results, setResults] = useState<CalculatorResults | null>(null)
  // Snapshot of the inputs the current `results` were computed from, so the
  // CRM/email payload never pairs live inputs with stale metrics after an edit.
  const [resultInputs, setResultInputs] = useState<CalculatorInputs | null>(null)
  const [showEmailCapture, setShowEmailCapture] = useState(false)
  const [email, setEmail] = useState('')
  // Chart state coordination - managed here and passed down to maintain single source of truth
  const [activeView, setActiveView] = useState<ChartView>('retention')
  const [chartScale, setChartScale] = useState<'linear' | 'log'>('linear')
  const [excludeVideoStarts, setExcludeVideoStarts] = useState(false)
  const [showLargestPostHookDrop, setShowLargestPostHookDrop] = useState(true)
  const [customBreakpointType, setCustomBreakpointType] = useState<'time' | 'percentile'>('time')
  const [customBreakpointValue, setCustomBreakpointValue] = useState(0)
  const [inputValidationMessage, setInputValidationMessage] = useState<string | null>(null)
  // Flag to trigger recalculation after auto-correction
  const [needsRecalculation, setNeedsRecalculation] = useState<boolean>(false)
  // Analytics timers and debounced interact - properly memoized to avoid dependency issues
  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])
  const _debouncedInteract = useMemo(() => debounce((...args: unknown[]) => {
    const interactionType = (args[0] as string) ?? 'input_change'
    sendResourceEvent('interact', {
      resourceType: 'calculator',
      resourceSlug: trackingSlug,
      engagementMs: getDeltaMs(),
      params: { interaction_type: interactionType }
    })
  }, 400), [getDeltaMs, trackingSlug])
  
  // Properly memoized trackInteract - now stable across renders so safe for dependencies
  const trackInteract = useCallback((interactionType: string) => {
    _debouncedInteract(interactionType)
  }, [_debouncedInteract])

  // Utility function for consistent time formatting
  const formatTime = useCallback((seconds: number, includeUnits = true): string => {
    // Always format in seconds, with consistent decimals
    const roundedValue = Math.round(seconds * 10) / 10
    
    // Remove trailing .0 if present
    const displayValue = roundedValue % 1 === 0 
      ? Math.floor(roundedValue) 
      : roundedValue
    
    return includeUnits ? `${displayValue}s` : String(displayValue)
  }, [])
  
  // Update breakpoint view count without immediate validation
  const updateBreakpointViewCount = useCallback((id: string, newViewCount: number) => {
    // Don't allow values below 0
    if (newViewCount < 0) return
    
    // Simply update the viewCount without validation - we'll validate on calculate
    setInputs(prev => ({
      ...prev,
      breakpoints: prev.breakpoints.map(bp => 
        bp.id === id ? { ...bp, viewCount: newViewCount } : bp
      )
    }))
    trackInteract('input_change')
  }, [setInputs, trackInteract]) // trackInteract now properly memoized - stable reference
  
  // Validate all breakpoints for consistency on blur or before calculation
  const validateBreakpoints = useCallback((): boolean => {
    // Sort breakpoints by time position
    const sortedBreakpoints = [...inputs.breakpoints].sort((a, b) => {
      const aTime = a.isPercentile ? (a.value / 100) * inputs.videoLength : a.value
      const bTime = b.isPercentile ? (b.value / 100) * inputs.videoLength : b.value
      return aTime - bTime
    })
    
    // Check if there are any invalid patterns (retention increasing over time)
    for (let i = 1; i < sortedBreakpoints.length; i++) {
      const currentBp = sortedBreakpoints[i]
      const prevBp = sortedBreakpoints[i - 1]
      
      if (currentBp && prevBp && currentBp.viewCount > prevBp.viewCount) {
        setInputValidationMessage("Error: Retention cannot increase over time. Please correct your viewer counts.")
        return false
      }
    }
    
    // All breakpoints are valid
    setInputValidationMessage(null)
    return true
  }, [inputs.breakpoints, inputs.videoLength])
  
  // Validate and auto-correct breakpoint inconsistencies
  const autoCorrectBreakpoints = useCallback((): boolean => {
    // Sort breakpoints by time position
    const sortedBreakpoints = [...inputs.breakpoints].sort((a, b) => {
      const aTime = a.isPercentile ? (a.value / 100) * inputs.videoLength : a.value
      const bTime = b.isPercentile ? (b.value / 100) * inputs.videoLength : b.value
      return aTime - bTime
    })
    
    // Check if there are any invalid patterns (retention increasing over time)
    let validationFailed = false
    // Use const as it's never reassigned
    const correctedBreakpoints = [...inputs.breakpoints]
    
    for (let i = 1; i < sortedBreakpoints.length; i++) {
      const currentBp = sortedBreakpoints[i]
      const prevBp = sortedBreakpoints[i - 1]
      
      if (currentBp && prevBp && currentBp.viewCount > prevBp.viewCount) {
        validationFailed = true
        
        // Find this breakpoint in our corrected array and adjust it
        const index = correctedBreakpoints.findIndex(bp => bp.id === currentBp.id)
        if (index !== -1 && correctedBreakpoints[index]) {
          const breakpointToUpdate = correctedBreakpoints[index]
          
          // Create a new valid Breakpoint object with the corrected view count
          correctedBreakpoints[index] = {
            id: breakpointToUpdate.id,
            label: breakpointToUpdate.label,
            value: breakpointToUpdate.value,
            isPercentile: breakpointToUpdate.isPercentile,
            viewCount: prevBp.viewCount // Cap at previous breakpoint's view count
          }
        }
      }
    }
    
    // If validation failed, update with corrected values and notify user
    if (validationFailed) {
      setInputs(prev => ({
        ...prev,
        breakpoints: correctedBreakpoints
      }))
      
      // Show validation message
      setInputValidationMessage("Some values were automatically adjusted to ensure retention doesn't increase over time. Review the corrected data before calculating.")
      return true
    }
    
    return false // No corrections needed
  }, [inputs.breakpoints, inputs.videoLength, setInputs, setInputValidationMessage])
  
  // Calculate results with validation
  const handleCalculate = useCallback(() => {
    // 1. Validate breakpoints first
    if (!validateBreakpoints()) {
      // Validation failed, message is set within validateBreakpoints.
      // Offer auto-correct.
      if (window.confirm("Retention cannot increase over time. Would you like to automatically adjust the values? This will cap viewer counts at the previous breakpoint's value.")) {
        const correctionsMade = autoCorrectBreakpoints()
        if (correctionsMade) {
          // 2. Set flag to trigger recalculation via useEffect
          setNeedsRecalculation(true)
          // Don't calculate immediately, let the effect handle it after state update
          return // Stop current execution
        }
        // If user confirms but no corrections were actually needed (shouldn't happen often), proceed to check if message needs clearing below
      }
      // If validation fails and user doesn't auto-correct, stop.
      // The validation message is already displayed.
      return 
    }
    
    // 3. Validation passed (or user declined auto-correct but maybe fixed manually)
    // Proceed with calculation using the current (and valid) inputs state.
    const newResults = calculateMetrics(inputs)
    setResults(newResults)
    setResultInputs(inputs)
    setShowEmailCapture(true)
    sendResourceEvent('interact', {
      resourceType: 'calculator',
      resourceSlug: trackingSlug,
      engagementMs: getDeltaMs(),
      params: { interaction_type: 'calculate' }
    })
    sendResourceEvent('complete', {
      resourceType: 'calculator',
      resourceSlug: trackingSlug,
      engagementMs: getTotalMs(),
      params: {
        completion_rate: newResults.advancedMetrics.completionRate,
        average_view_duration: newResults.advancedMetrics.averageViewDuration
      }
    })
    // Ensure message is cleared on successful calculation
    setInputValidationMessage(null) 
    
  }, [inputs, validateBreakpoints, autoCorrectBreakpoints, setResults, setResultInputs, setShowEmailCapture, setInputValidationMessage, setNeedsRecalculation, getDeltaMs, getTotalMs, trackingSlug])

  // NEW EFFECT: Handle recalculation after state update from auto-correction
  useEffect(() => {
    if (needsRecalculation) {
      // Reset the flag immediately
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional client-side state update inside an effect (SSR-safe staging of derived UI state)
      setNeedsRecalculation(false)
      
      // Re-validate the *current* state after autoCorrectBreakpoints updated it
      if (validateBreakpoints()) {
        // If valid, proceed with calculation
        const newResults = calculateMetrics(inputs)
        setResults(newResults)
        setResultInputs(inputs)
        setShowEmailCapture(true)
        // Clear any lingering validation/correction message
        setInputValidationMessage(null) 
      } else {
        // This case *shouldn't* happen if autoCorrectBreakpoints worked, 
        // but log it if it does. The validation message will remain set.
        clientLogger.error('Validation failed even after auto-correction attempt.', { component: 'VideoDropoffCalculator' })
      }
    }
  }, [needsRecalculation, inputs, validateBreakpoints, setResults, setResultInputs, setShowEmailCapture, setInputValidationMessage])

  // Add custom breakpoint function to ensure it's inserted with a valid view count
  const addCustomBreakpoint = useCallback((): void => {
    if (inputs.platform !== 'custom') return
    
    // Use custom value from state
    const newValue = customBreakpointValue ?? (customBreakpointType === 'time' ? 10 : 25)
    const isPercentile = customBreakpointType === 'percentile'
    
    // Convert to actual time value for sorting
    const newTimeValue = isPercentile ? (newValue / 100) * inputs.videoLength : newValue
    
    // Get all current breakpoints sorted by time
    const sortedBreakpoints = [...inputs.breakpoints].sort((a, b) => {
      const aTime = a.isPercentile ? (a.value / 100) * inputs.videoLength : a.value
      const bTime = b.isPercentile ? (b.value / 100) * inputs.videoLength : b.value
      return aTime - bTime
    })
    
    // Find where this new breakpoint would fit in the timeline
    let prevBreakpoint: Breakpoint | null = null // Explicitly type as Breakpoint | null
    let nextBreakpoint: Breakpoint | null = null // Explicitly type as Breakpoint | null
    
    // Use for...of loop instead of indexed for loop
    for (const currentBreakpoint of sortedBreakpoints) {
      if (!currentBreakpoint) continue
      
      const breakpointTime = currentBreakpoint.isPercentile 
        ? (currentBreakpoint.value / 100) * inputs.videoLength 
        : currentBreakpoint.value
      
      if (Math.abs(breakpointTime - newTimeValue) < 0.001) {
        // This time point already exists - don't add a duplicate
        setInputValidationMessage("A breakpoint at this exact time already exists")
        setTimeout(() => setInputValidationMessage(null), 3000)
        return
      }
      
      if (breakpointTime < newTimeValue) {
        prevBreakpoint = currentBreakpoint
      } else if (breakpointTime > newTimeValue && !nextBreakpoint) {
        nextBreakpoint = currentBreakpoint
        break
      }
    }
    
    // Calculate a valid view count 
    // It must be <= previous breakpoint and >= next breakpoint
    let estimatedViewCount: number
    
    if (prevBreakpoint && nextBreakpoint) {
      // Linearly interpolate between surrounding breakpoints
      const prevTime = prevBreakpoint.isPercentile 
        ? (prevBreakpoint.value / 100) * inputs.videoLength 
        : prevBreakpoint.value
      
      const nextTime = nextBreakpoint.isPercentile
        ? (nextBreakpoint.value / 100) * inputs.videoLength
        : nextBreakpoint.value
      
      // Avoid division by zero if times are identical
      const timeDiff = nextTime - prevTime
      if (timeDiff === 0) {
          estimatedViewCount = prevBreakpoint.viewCount
      } else {
          const timeFraction = (newTimeValue - prevTime) / timeDiff
          estimatedViewCount = Math.round(
            prevBreakpoint.viewCount - (timeFraction * (prevBreakpoint.viewCount - nextBreakpoint.viewCount))
          )
      }
    } else if (prevBreakpoint) {
      // This is after all existing breakpoints, estimate based on trend
      estimatedViewCount = Math.round(prevBreakpoint.viewCount * 0.8) // 20% drop as estimate
    } else if (nextBreakpoint) {
      // This is before all existing breakpoints (except start)
      // Ensure nextBreakpoint.viewCount is not exceeded
      estimatedViewCount = Math.min(inputs.totalViews, Math.round(nextBreakpoint.viewCount * 1.25)) // 25% higher as estimate, capped at totalViews
    } else { 
      // First breakpoint after start (only start exists)
      estimatedViewCount = Math.round(inputs.totalViews * 0.5) // 50% as default
    }
    
    // Ensure estimatedViewCount is non-negative and not more than totalViews or previous breakpoint
    estimatedViewCount = Math.max(0, estimatedViewCount)
    if (prevBreakpoint) {
        estimatedViewCount = Math.min(estimatedViewCount, prevBreakpoint.viewCount)
    }
    if (nextBreakpoint) {
        // Although we interpolate, ensure it's not *less* than the next point if prev point didn't exist
        estimatedViewCount = Math.max(estimatedViewCount, nextBreakpoint.viewCount)
    }
    estimatedViewCount = Math.min(estimatedViewCount, inputs.totalViews) // Final cap

    // Create the new breakpoint
    const newBreakpoint: Breakpoint = {
      id: `custom-${Date.now()}`,
      label: isPercentile ? `${newValue}% Watched` : `${newValue} Seconds`,
      value: newValue,
      isPercentile,
      viewCount: estimatedViewCount
    }
    
    setInputs(prev => ({
      ...prev,
      breakpoints: [...prev.breakpoints, newBreakpoint]
    }))
    trackInteract('add_breakpoint')
    
    // Reset custom value
    setCustomBreakpointValue(0)
  }, [
    inputs.platform, 
    inputs.videoLength, 
    inputs.totalViews, 
    inputs.breakpoints, 
    customBreakpointType, 
    customBreakpointValue, 
    setInputValidationMessage,
    setInputs,
    trackInteract // Now properly memoized - stable reference safe to include
  ])

  // Remove custom breakpoint
  const removeBreakpoint = useCallback((id: string): void => {
    setInputs(prev => ({
      ...prev,
      breakpoints: prev.breakpoints.filter(bp => bp.id !== id)
    }))
    trackInteract('remove_breakpoint')
  }, [setInputs, trackInteract])

  // Check if inputs are valid for calculation
  const isValidForCalculation = useMemo((): boolean => {
    if (inputs.totalViews <= 0 || inputs.videoLength <= 0) {
      return false
    }
    
    // Ensure we have at least 2 breakpoints (start and at least one more)
    if (inputs.breakpoints.length < 2) {
      return false
    }
    
    // Ensure all breakpoints have non-negative view counts
    return inputs.breakpoints.every(bp => bp.viewCount >= 0)
  }, [inputs.totalViews, inputs.videoLength, inputs.breakpoints])

  // Email template for results
  const emailTemplate = useMemo(() => {
    return {
      templateAlias: 'welcome',
      to: '',
      from: process.env.NEXT_PUBLIC_EMAIL_FROM ?? 'info@adsights.ai',
      templateModel: {
        base: {
          name: email ? email.split('@')[0]?.trim() ?? '' : '',
          logo_url: 'https://www.adsights.ai/logo-square.png',
          product_name: 'AdSights',
          product_url: 'https://www.adsights.ai',
          support_email: 'support@adsights.ai',
          sender_name: 'The AdSights Team',
          company_name: 'AdSights Inc.',
          company_address: '254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702',
          email: email ?? ''
        },
        custom: {
          calculator_type: 'Video Drop-off',
          calculator_results: results ? {
            metrics: results,
            inputs: inputs
          } : null
        }
      }
    }
  }, [email, results, inputs])

  // Email capture configuration
  const emailCaptureConfig: EmbeddedEmailCaptureProps = {
    preset: 'indigo',
    content: {
      title: {
        text: "Get Your Video Retention Analysis"
      },
      description: {
        text: "We'll send you a detailed breakdown of your metrics and personalized recommendations."
      },
      button: {
        text: "Get Analysis",
        loadingText: "Preparing Analysis...",
        icon: null,
        className: "flex w-auto items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
      },
      input: {
        label: "Work Email",
        placeholder: "Enter your work email"
      },
      messages: {
        success: "Analysis sent!",
        error: "Please enter a valid email address."
      }
    },
    template: emailTemplate,
    analytics: {
      enabled: true,
      category: 'calculators',
      action: 'email_capture',
      label: `${trackingSlug.replace(/-/g, '_')}_results`,
      elementId: `${trackingSlug}-email-form`,
      pageLocation: heading
    },
    crm: {
      tags: ['calculator_results', 'video_dropoff'],
      source: trackingSlug.replace(/-/g, '_'),
      // Read from the `resultInputs` snapshot, never live `inputs`, so an edit
      // after Calculate can't store a mismatched input/metric pair.
      customFields: {
        calculator_type: 'Video Drop-off',
        calculator_platform: resultInputs?.platform,
        calculator_video_length: resultInputs?.videoLength,
        calculator_total_views: resultInputs?.totalViews,
        calculator_completion_rate: results?.advancedMetrics.completionRate ?? 0
      }
    },
    onSubmit: (submittedEmail) => setEmail(submittedEmail)
  }

  // COORDINATED CHART DATA CALCULATIONS - calculated once here and passed to ResultsSection
  // This ensures all chart components receive consistent, properly processed data
  
  // Calculate largest post-hook drop-off point
  const largestPostHookDrop = useMemo(() => {
    if (!results?.dropoffRates || results.dropoffRates.length < 3) return null

    // Define the hook section end (first 15% of video or 5 seconds, whichever is less)
    const hookEndTime = Math.min(5, inputs.videoLength * 0.15)

    // Start from index 1 to skip the first point (which is always 0)
    // Filter drop-off rates to only include points after the hook
    const postHookRates = results.dropoffRates.slice(1).filter(item => {
      if (!item?.breakpoint) return false

      const dropPointTime = item.breakpoint.isPercentile
        ? (item.breakpoint.value / 100) * inputs.videoLength
        : item.breakpoint.value

      return dropPointTime > hookEndTime
    })

    if (!postHookRates.length) return null

    // Find the point with the largest drop-off after the hook
    let maxDrop = -1
    let maxDropPoint: DropoffRateItem | null = null

    for (const item of postHookRates) {
      if (item && typeof item.value === 'number' && item.value > maxDrop) {
        maxDrop = item.value
        maxDropPoint = item
      }
    }
    
    // Use optional chaining for the check
    if (!maxDropPoint?.breakpoint) return null 
    
    // Calculate time position for this breakpoint
    const maxDropTime = maxDropPoint.breakpoint.isPercentile
      ? (maxDropPoint.breakpoint.value / 100) * inputs.videoLength
      : maxDropPoint.breakpoint.value

    return {
      time: maxDropTime,
      percentage: (maxDropTime / inputs.videoLength) * 100,
      dropoffRate: maxDropPoint.value,
      label: maxDropPoint.breakpoint.label,
      formattedTime: formatTime(maxDropTime)
    }
  }, [results, inputs.videoLength, formatTime])

  // Prepare processed retention and drop-off data for charts
  // Note: prepareChartData handles outlier detection internally for drop-off data
  const chartData = useMemo(() => {
    if (!results) return { retention: [], dropoff: [] }

    // Prepare full data first - prepareChartData handles outlier detection internally
    const fullRetentionData = prepareChartData(results.retentionRates, true, inputs.videoLength, chartScale)
    const fullDropoffData = prepareChartData(results.dropoffRates, false, inputs.videoLength, chartScale)

    // If excludeVideoStarts is true, filter appropriately
    if (excludeVideoStarts) {
      // For retention data, get all points except the first (video start)
      const retentionWithoutStart = fullRetentionData.filter((_, index) => index > 0)

      // For drop-off, we already start from first interval (not video start)
      return {
        retention: retentionWithoutStart,
        dropoff: fullDropoffData
      }
    }

    return {
      retention: fullRetentionData,
      dropoff: fullDropoffData
    }
  }, [results, inputs.videoLength, chartScale, excludeVideoStarts])

  return (
    <div className="mx-auto max-w-4xl" role="main" aria-labelledby="calculator-heading">
      <div className="rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        {/* Calculator Header */}
        <div className="px-8 pt-8">
          <h2 id="calculator-heading" className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            {heading}
          </h2>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
            {subheading}
          </p>
        </div>

        {/* Platform Selection */}
        <PlatformSelector
          selectedPlatform={inputs.platform}
          onPlatformChange={(platform) => {
            setInputs(prev => ({ ...prev, platform }))
            trackInteract('platform_change')
          }}
        />

        {/* Basic Inputs */}
        <BasicInputs
          videoLength={inputs.videoLength}
          totalViews={inputs.totalViews}
          onVideoLengthChange={(value) => {
            setInputs(prev => ({ ...prev, videoLength: value }))
            trackInteract('input_change')
          }}
          onTotalViewsChange={(value) => {
            setInputs(prev => ({ ...prev, totalViews: value }))
            trackInteract('input_change')
          }}
        />

        {/* Breakpoints Section */}
        <div className="mt-8 px-8">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Video Breakpoints
            </h3>
            {inputs.platform === 'custom' && (
              <div className="flex items-center gap-2">
                <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setCustomBreakpointType('time')}
                    className={cn(
                      "rounded-l-lg px-3 py-1.5 text-sm font-medium",
                      customBreakpointType === 'time'
                        ? "bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400"
                        : "bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                    )}
                  >
                    Time-based
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomBreakpointType('percentile')}
                    className={cn(
                      "rounded-r-lg px-3 py-1.5 text-sm font-medium",
                      customBreakpointType === 'percentile'
                        ? "bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400"
                        : "bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                    )}
                  >
                    Percentile
                  </button>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={customBreakpointValue}
                    onChange={(e) => setCustomBreakpointValue(Math.max(0, Math.min(customBreakpointType === 'percentile' ? 100 : inputs.videoLength, Number(e.target.value))))}
                    min={0}
                    max={customBreakpointType === 'percentile' ? 100 : inputs.videoLength}
                    className="w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 text-center"
                    placeholder={customBreakpointType === 'time' ? "Seconds" : "Percent"}
                  />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {customBreakpointType === 'time' ? 'sec' : '%'}
                  </span>
                  
                  <button
                    type="button"
                    onClick={addCustomBreakpoint}
                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    disabled={inputs.breakpoints.length >= 10}
                  >
                    <RiAddLine className="-ml-0.5 mr-1.5 h-4 w-4" />
                    Add Breakpoint
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Enter the number of viewers at each breakpoint in your video analytics.
          </p>

          <div className="mt-4 mb-8 space-y-4">
            <BreakpointsTable
              breakpoints={inputs.breakpoints}
              videoLength={inputs.videoLength}
              totalViews={inputs.totalViews}
              isCustomPlatform={inputs.platform === 'custom'}
              onUpdateBreakpointViewCount={updateBreakpointViewCount}
              onValidateBreakpoints={validateBreakpoints}
              onRemoveBreakpoint={removeBreakpoint}
              formatTime={formatTime}
            />
          </div>
        </div>

        {/* Calculate Button */}
        <div className="border-t border-gray-200 bg-gray-50 px-8 py-6 dark:border-gray-800 dark:bg-gray-900/50 rounded-b-2xl">
          <button
            onClick={handleCalculate}
            disabled={!isValidForCalculation}
            className={cn(
              "w-full rounded-lg px-4 py-3 text-center text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]",
              isValidForCalculation 
                ? "bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-600" 
                : "bg-gray-400 cursor-not-allowed"
            )}
            aria-label="Calculate video retention metrics"
          >
            <span className="flex items-center justify-center">
              <RiBarChartLine className="mr-2 h-5 w-5" />
              Analyze Video Metrics
            </span>
          </button>
          
          {!isValidForCalculation && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400 text-center">
              Please enter valid data for all fields. Ensure video length, total views, and at least one breakpoint are provided.
            </p>
          )}
          
          {inputValidationMessage && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md text-sm dark:bg-yellow-900/30 dark:border-yellow-800 dark:text-yellow-200 animate-pulse">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mr-2 flex-shrink-0">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                {inputValidationMessage}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Section */}
      {results && (
        <ResultsSection
          results={results}
          inputs={inputs}
          formatTime={formatTime}
          // Coordinated chart state - managed here and passed down for consistency
          activeView={activeView}
          setActiveView={setActiveView}
          chartScale={chartScale}
          setChartScale={setChartScale}
          excludeVideoStarts={excludeVideoStarts}
          setExcludeVideoStarts={setExcludeVideoStarts}
          showLargestPostHookDrop={showLargestPostHookDrop}
          setShowLargestPostHookDrop={setShowLargestPostHookDrop}
          // Coordinated calculated data - computed once here for consistency
          largestPostHookDrop={largestPostHookDrop}
          chartData={chartData}
        />
      )}

      {/* Email Capture Section */}
      {showEmailCapture && results && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="rounded-lg bg-indigo-50 dark:bg-indigo-900/20 mt-8"
        >
          <EmbeddedEmailCapture {...emailCaptureConfig} />
        </motion.div>
      )}

      {/* Related concepts — static SSR-visible cross-links. See the
          VIDEO_DROPOFF_RELATED_* constants at the top of this file
          for the rationale on why these are static rather than
          relying on CalculatorLayout's client-fetched RelatedContent. */}
      <section
        aria-labelledby="video-dropoff-related-concepts"
        className="mt-12 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800"
      >
        <h3
          id="video-dropoff-related-concepts"
          className="text-2xl font-semibold text-gray-900 dark:text-white mb-3"
        >
          Related concepts &amp; calculators
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Retention curves sit inside a larger video-creative analysis
          framework. Use these definitions and calculators to grade the
          leak, size the cost impact, and plan the test that fixes it.
        </p>
        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Glossary terms
            </h4>
            <ul className="space-y-3">
              {VIDEO_DROPOFF_RELATED_GLOSSARY.map((item) => (
                <li key={item.slug} className="text-sm">
                  <Link
                    href={`/resources/glossary/${item.slug}`}
                    className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {item.label}
                  </Link>
                  <span className="text-gray-600 dark:text-gray-400"> — {item.note}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Other calculators
            </h4>
            <ul className="space-y-3">
              {VIDEO_DROPOFF_RELATED_TOOLS.map((item) => (
                <li key={item.slug} className="text-sm">
                  <Link
                    href={`/resources/tools/calculators/${item.slug}`}
                    className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {item.label}
                  </Link>
                  <span className="text-gray-600 dark:text-gray-400"> — {item.note}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <div className="mt-12 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
          Frequently Asked Questions
        </h3>
        <FAQ
          items={faqItems.map(item => ({ ...item }))}
          type="single"
          disableSchema={true}
          features={{ showTitle: false }}
          theme={{ size: "lg" }}
        />
      </div>
    </div>
  )
}

export default VideoDropoffCalculator